'use client'

import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import TooltipHost from '@/core/components/tooltip-host'
import { cn } from '@/core/utils/common'
import { useHydratedStore } from '@/core/utils/zustand-persist'

import LayerHost from '@/features/veluvit/components/overlay/layer-host'
import PaymentFlowHost from '@/features/veluvit/components/wallet/payment-flow-host'
import DataProvider from '@/features/veluvit/components/providers/data-provider'
import { FixedSlotContext } from '@/features/veluvit/components/shell/fixed-bar'
import BottomTabs from '@/features/veluvit/components/shell/bottom-tabs'
import Gnb from '@/features/veluvit/components/shell/gnb'
import SideNav, { SIDE_W } from '@/features/veluvit/components/shell/side-nav'
import { RailSlotContext } from '@/features/veluvit/components/shell/rail-slot'
import RewardBurstHost from '@/features/veluvit/components/shell/reward-burst-host'
import { registerRouter } from '@/features/veluvit/lib/navigation'
import { closeAll } from '@/features/veluvit/stores/overlay.store'
import { useUiStore } from '@/features/veluvit/stores/ui.store'

import type { ReactNode } from 'react'
import type { FetcherResponse } from '@/core/lib/fetcher/core'
import type { Bootstrap } from '@/features/veluvit/types/api.types'

type ViewMode = {
  full: boolean
  noscroll: boolean
  pbGnb: boolean
  deskhome: boolean
  rail: boolean
  side: boolean
  gnb: boolean
}

/** 경로별 본문 모드 — 레퍼런스 각 ROUTES 가 #view 에 붙이던 클래스(full·noscroll·pb-gnb·deskhome) */
function viewMode(pathname: string): ViewMode {
  const home = pathname === '/' || pathname === '/story'
  const chat = pathname.startsWith('/chat/')
  const explore = pathname.startsWith('/explore')
  // 스튜디오 편집 — 단계 메뉴·폼·미리보기가 각자 스크롤한다
  const studioEdit =
    /^\/studio-renew\/(work|character|world|status|command)\//.test(pathname)
  // 스튜디오는 앱 메뉴 없이 자기 머리줄(목록 ← 스튜디오 · 편집 EditorBar)만 쓴다
  const studio =
    pathname === '/studio-renew' || pathname.startsWith('/studio-renew/')
  return {
    full:
      chat ||
      explore ||
      pathname.startsWith('/c/') ||
      pathname.startsWith('/lp/'),
    noscroll: chat || explore || studioEdit,
    pbGnb:
      home ||
      pathname.startsWith('/ranking') ||
      pathname.startsWith('/chats') ||
      pathname.startsWith('/mission') ||
      pathname === '/search' ||
      pathname === '/my' ||
      pathname === '/attendance' ||
      pathname.startsWith('/payment/'),
    deskhome: home,
    // PC 대화방 사이드 레일(284px) — 2026-10-08 컬렉션 레일을 숨기며 자리도 비웠다(그 폭만큼 장면 무대가 커진다). 되살릴 땐 chat 으로
    rail: false,
    // PC 왼쪽 대화 패널 · 상단 막대 — 스튜디오에서만 뺀다(2026-11-02)
    side: !studio,
    gnb: !studio,
  }
}

/** 하단 탭 접기 — 맨 위 근처(TAB_TOP 안)는 늘 펼친다. 그 밖에선 방향으로: 같은 방향으로 TAB_DOWN 만큼 내리면 접고, TAB_UP 만큼 올리면 펼친다.
    올리는 쪽을 조금 더 크게 둬 손가락 떨림 · 관성 끝의 되튐에 펼쳐지지 않게 한다 */
const TAB_TOP = 72
const TAB_DOWN = 8
const TAB_UP = 24

export default function AppShell({
  children,
  initial,
  epoch,
  initialNow,
}: {
  children: ReactNode
  initial?: FetcherResponse<Bootstrap>
  epoch?: string
  initialNow?: number
}) {
  const pathname = usePathname()
  const router = useRouter()
  // 스크롤 칸(#view) — ref 가 아니라 상태로 받는다. 첫 접속엔 DataProvider 가 로딩 화면만 그려 #view 가 아직 없다.
  // ref 로 읽으면 아래 effect 들이 null 을 보고 끝나 버려 경로가 바뀌기 전까지 스크롤 감지(하단 탭 접기)가 붙지 않았다(2026-10-15)
  const [view, setView] = useState<HTMLDivElement | null>(null)
  const [fixedSlot, setFixedSlot] = useState<HTMLDivElement | null>(null)
  const [railSlot, setRailSlot] = useState<HTMLElement | null>(null)
  const scrollMemory = useRef(new Map<string, number>())
  const scrollTop = useRef(0)
  const lastPath = useRef<string | null>(null)
  const sideCollapsed = useHydratedStore(
    useUiStore,
    (s) => s.sideCollapsed,
    false
  )
  const screenRef = useRef<HTMLDivElement>(null)
  const mode = viewMode(pathname)

  // PC 왼쪽 패널 폭을 화면 틀 변수로 — 본문 · 상단 막대 · 하단 고정 칸이 이 값만큼 오른쪽에서 시작한다
  useEffect(() => {
    screenRef.current?.style.setProperty(
      '--side-w',
      `${!mode.side ? 0 : sideCollapsed ? SIDE_W.closed : SIDE_W.open}px`
    )
  }, [sideCollapsed, mode.side])

  useEffect(() => {
    registerRouter({
      push: (href) => router.push(href),
      back: () => router.back(),
    })
  }, [router])

  // 목록 → 상세 → 뒤로 왔을 때 스크롤 복원, 경로가 바뀌면 레이어는 닫는다 (레퍼런스 scrollMem · hashchange)
  useLayoutEffect(() => {
    if (!view) return
    if (lastPath.current && lastPath.current !== pathname) {
      scrollMemory.current.set(lastPath.current, scrollTop.current)
      closeAll()
    }
    lastPath.current = pathname
    const saved = scrollMemory.current.get(pathname) || 0
    view.scrollTo({ top: saved, behavior: 'instant' })
    scrollTop.current = saved
    // 이미지가 늦게 자리를 잡는 목록은 한 프레임 뒤에 한 번 더 맞춘다
    const frame = requestAnimationFrame(() => {
      if (saved && view.scrollTop !== saved)
        view.scrollTo({ top: saved, behavior: 'instant' })
    })
    return () => cancelAnimationFrame(frame)
  }, [pathname, view])

  // 모바일 하단 탭: 어디서든 내리면 라벨이 사라지며 줄어들고, 조금 올리면 펼쳐진다(스크롤 방향 기준). 맨 위 근처는 늘 펼침. 릴스는 진입부터 줄인다.
  useEffect(() => {
    const desk = window.matchMedia('(min-width: 1001px)')
    // 경로가 바뀌면 복원된 스크롤 위치(위 layout effect)에서 새로 센다 — 복원 점프를 '내림'으로 읽지 않게
    let lastY = view?.scrollTop || 0
    /** 지금 방향으로 이어서 움직인 거리 — 내리면 +, 올리면 −. 방향이 바뀌면 0 부터 */
    let travel = 0
    const sync = () => {
      const ui = useUiStore.getState()
      scrollTop.current = view?.scrollTop || 0
      if (desk.matches) {
        if (ui.tabMini) ui.set({ tabMini: false })
        return
      }
      if (pathname.startsWith('/explore')) {
        if (!ui.tabMini) ui.set({ tabMini: true })
        return
      }
      // iOS 고무줄(맨 위 · 맨 아래 너머) 구간은 잘라서 본다 — 바닥에서 튕겨 올라오는 것을 '올림'으로 읽지 않게
      const max = view ? view.scrollHeight - view.clientHeight : 0
      const y = Math.min(Math.max(scrollTop.current, 0), Math.max(max, 0))
      const dy = y - lastY
      lastY = y
      if (y < TAB_TOP) {
        travel = 0
        if (ui.tabMini) ui.set({ tabMini: false })
        return
      }
      if (!dy) return
      travel = dy > 0 ? Math.max(travel, 0) + dy : Math.min(travel, 0) + dy
      if (!ui.tabMini && travel > TAB_DOWN) ui.set({ tabMini: true })
      else if (ui.tabMini && travel < -TAB_UP) ui.set({ tabMini: false })
    }
    sync()
    view?.addEventListener('scroll', sync, { passive: true })
    desk.addEventListener('change', sync)
    return () => {
      view?.removeEventListener('scroll', sync)
      desk.removeEventListener('change', sync)
    }
  }, [pathname, view])

  return (
    <div className="fixed inset-0 flex justify-center bg-shell text-c15 leading-[1.5] text-t1 over10:bg-none">
      <div
        id="screen"
        ref={screenRef}
        // 이미지·링크의 기본 드래그(반투명 복사본)를 막는다 — CSS(-webkit-user-drag)를 모르는 Firefox 용. 파일 끌어놓기(drop)는 영향 없다
        onDragStart={(event) => {
          if ((event.target as HTMLElement).closest?.('img, a'))
            event.preventDefault()
        }}
        className={cn(
          'relative isolate h-dvh w-full max-w-[480px] overflow-clip bg-background over10:max-w-none',
          // 테마 바탕 무늬(망점 · 격자 · 주사선 …, lib/palette PATTERNS) — 기본은 없음
          '[background-image:var(--bg-pattern,none)] [background-size:var(--bg-size,auto)]',
          // PC 상세 2단 컬럼 폭 — 본문 그리드와 하단 CTA 가 같은 기준으로 정렬한다
          'over10:[--dt-col-gap:32px] over10:[--dt-col-l:min(440px,calc((100dvh_-_180px)*39/47))] over10:[--dt-col-r:650px]',
          // PC 왼쪽 패널 폭 — 접으면 아래 effect 가 인라인으로 덮어쓴다(서버 렌더와 어긋나지 않게 클래스엔 펼친 폭만)
          mode.side ? 'over10:[--side-w:256px]' : 'over10:[--side-w:0px]'
        )}
      >
        <DataProvider initial={initial} epoch={epoch} initialNow={initialNow}>
          {/* PC 상단 내비 / 모바일 하단 탭 — 같은 메뉴 목록(nav-items), 화면 폭으로 한쪽만 보인다 */}
          {mode.gnb && <Gnb logo={!mode.side} />}
          {mode.side && <SideNav />}
          <BottomTabs />
          {mode.rail && (
            <aside
              ref={setRailSlot}
              aria-label="사이드 패널"
              className="absolute top-14.5 bottom-0 left-0 z-20 hidden w-71 over10:left-(--side-w) over10:transition-[left] over10:duration-[240ms] over10:ease-smooth overflow-y-auto scrollbar-none bg-background px-3.5 pt-4 pb-7.5 shadow-[inset_-1px_0_0_rgb(255_255_255/0.08)] over10:block"
            />
          )}
          <RailSlotContext.Provider value={railSlot}>
            <FixedSlotContext.Provider value={fixedSlot}>
              <div
                ref={setView}
                id="view"
                className={cn(
                  'absolute inset-0 scrollbar-none',
                  mode.noscroll
                    ? 'overflow-clip'
                    : 'overflow-x-hidden overflow-y-auto',
                  // 위 여백은 기기 안전 영역(노치 · 상태 표시줄)만큼만 — 브라우저는 0, 앱(Capacitor · iOS)에서만 생긴다. 예전엔 34px 고정(레퍼런스의 가짜 상태 표시줄)
                  mode.full ? 'pt-0' : 'pt-(--safe-top)',
                  // 모바일은 떠 있는 하단 탭 자리(110px)를 비운다
                  mode.pbGnb && 'pb-27.5 over10:pb-10',
                  // PC — 스크롤하는 화면은 상단 내비(58px) 밑으로도 내용이 지나가게 위 여백으로 비운다(내비 유리가 실제로 내용을 비춘다 · 붙는 머리줄과 같은 유리가 된다).
                  // 스크롤하지 않는 화면(대화방 · 탐색)은 안쪽이 absolute 로 #view 를 채워 예전처럼 내비 아래에서 시작한다. 내비가 없는 스튜디오는 맨 위부터
                  !mode.gnb
                    ? 'over10:top-0 over10:pt-0'
                    : mode.noscroll
                      ? 'over10:top-14.5 over10:pt-0'
                      : 'over10:top-0 over10:pt-14.5',
                  // PC — 왼쪽 패널(--side-w) 오른쪽을 꽉 채운다. 대화방은 그 옆 사이드 패널(284px)만큼 더
                  mode.rail
                    ? 'over10:left-[calc(var(--side-w)+284px)]'
                    : 'over10:left-(--side-w)',
                  // 왼쪽 패널을 접고 펼칠 때 패널 폭과 같은 시간 · 곡선으로 함께 민다(side-nav)
                  'over10:transition-[left] over10:duration-[240ms] over10:ease-smooth'
                )}
              >
                {children}
              </div>
            </FixedSlotContext.Provider>
          </RailSlotContext.Provider>
          <div
            ref={setFixedSlot}
            className={cn(
              'absolute inset-x-0 bottom-0 z-[45] over10:transition-[left] over10:duration-[240ms] over10:ease-smooth',
              mode.rail
                ? 'over10:left-[calc(var(--side-w)+284px)]'
                : 'over10:left-(--side-w)'
            )}
          />

          <RewardBurstHost />
          <PaymentFlowHost />
          <LayerHost />
          <TooltipHost />
        </DataProvider>
      </div>
    </div>
  )
}
